import { OpenBankingConnectionStatus } from '@seapoint/server/enums';
import axios from 'axios';
import { useSearchParams } from 'react-router';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import z from 'zod';
import { AccountView, CompanyView, InstitutionView } from '../types/entity-types';
import { parseYapilyError } from '../utils/parse-yapily-error';

const connectionStatusSchema = z
  .enum([OpenBankingConnectionStatus.CONNECTED, OpenBankingConnectionStatus.DISCONNECTED])
  .nullable()
  .catch(null);

const connectedCountSchema = z.coerce.number().nullable().catch(null);

export interface SyncResponse {
  accounts: AccountView[];
  countTransactions: number;
}

export const useOpenBankingAPI = (company: CompanyView) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const connectionStatus = connectionStatusSchema.parse(searchParams.get('status'));
  const connectedCount = connectedCountSchema.parse(searchParams.get('connectedCount'));

  const companyCountry = company.legalEntity.country;

  const {
    data: institutionsData,
    isLoading: isLoadingInstitutions,
    error: institutionsError,
  } = useSWRImmutable<{
    institutions: InstitutionView[];
  }>(companyCountry ? `/api/open-banking/institutions?country=${companyCountry}` : null);
  const {
    data: authLinkData,
    trigger: getAuthLink,
    isMutating: isFetchingAuthLink,
    error: authLinkError,
  } = useSWRMutation<
    { link: string },
    Error,
    string,
    { companyId: string; institutionId: string; userName: string; psuId?: string }
  >(`/api/open-banking/auth-link`, authLinkFetcher);

  const {
    data: reAuthLinkData,
    trigger: getReAuthLink,
    isMutating: isFetchingReAuthLink,
    error: reAuthLinkError,
  } = useSWRMutation<{ link: string }, Error, string, { companyId: string; institutionId: string; psuId?: string }>(
    `/api/yapily/re-auth-link`,
    reAuthLinkFetcher,
  );

  const clearUrlParams = () => {
    setSearchParams({});
  };

  return {
    data: {
      institutionsData,
      authLinkData,
      reAuthLinkData,
      connectionStatus,
      connectedCount,
    },
    isLoadingData: {
      isLoadingInstitutions,
      isFetchingAuthLink,
      isFetchingReAuthLink,
    },
    errorData: {
      institutionsError,
      authLinkError,
      reAuthLinkError,
      isConnectedError: connectionStatus === OpenBankingConnectionStatus.DISCONNECTED,
    },
    mutations: {
      clearUrlParams,
      getAuthLink,
      getReAuthLink,
    },
  };
};

const authLinkFetcher = async (
  url: string,
  { arg }: { arg: { companyId: string; institutionId: string; userName: string; psuId?: string } },
) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Build query parameters
  const params = new URLSearchParams({
    companyId: arg.companyId,
    institutionId: arg.institutionId,
    userName: arg.userName,
  });

  // Add psuId if provided
  if (arg.psuId) {
    params.append('psuId', arg.psuId);
  }

  try {
    const response = await axios.get(`${url}?${params.toString()}`, options);

    return response.data;
  } catch (error) {
    // Parse Yapily error to extract user-friendly message
    const errorMessage = parseYapilyError(error);

    // If we got a parsed message, throw that; otherwise rethrow original error
    if (errorMessage) {
      throw new Error(errorMessage);
    }

    throw error;
  }
};

const reAuthLinkFetcher = async (
  url: string,
  { arg }: { arg: { companyId: string; institutionId: string; psuId?: string } },
) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Build query parameters
  const params = new URLSearchParams({
    companyId: arg.companyId,
    institutionId: arg.institutionId,
  });

  // Add psuId if provided
  if (arg.psuId) {
    params.append('psuId', arg.psuId);
  }

  try {
    const response = await axios.get(`${url}?${params.toString()}`, options);

    return response.data;
  } catch (error) {
    // Parse Yapily error to extract user-friendly message
    const errorMessage = parseYapilyError(error);

    // If we got a parsed message, throw that; otherwise rethrow original error
    if (errorMessage) {
      throw new Error(errorMessage);
    }

    throw error;
  }
};
